import React, { useRef, useCallback, useMemo } from 'react';
import { StyleSheet, Pressable, Animated, Platform } from 'react-native';
import { useChatStore, useAuthStore, useChatInputStore, useToastStore } from '@/store';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import socketManager from '@/lib/socket';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';

const BUTTON_SIZE = 35;
const EMPTY_MODEL_IDS = Object.freeze([]);

const styles = StyleSheet.create({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  buttonDisabled: {
    opacity: 0.4,
    ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
  },
});

// Helper to check if a model ID is Arya
const isAryaModel = (modelId) => {
  if (!modelId) return false;
  const lowerId = modelId.toLowerCase();
  return lowerId.includes('arya');
};

function FastModeButton({ 
  isDisabled = false,
  conversationId = null,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const userId = useAuthStore(state => state.user?.id);
  const contextId = conversationId || 'new';
  
  // Get selected model IDs for the current context
  const selectedModelIds = useChatInputStore(state =>
    state.inputStates?.[contextId]?.selectedModelIds ?? EMPTY_MODEL_IDS
  );
  
  // Fast mode is only available for Auto (no models selected) or Arya models
  const isModelCompatible = useMemo(() => {
    // Auto mode (no model selected) - fast mode allowed
    if (selectedModelIds.length === 0) return true;
    // Check if all selected models are Arya
    return selectedModelIds.every(isAryaModel);
  }, [selectedModelIds]);
  
  // For existing conversations, get fast mode from chat flags
  // For new conversations, get it from the chat input store
  const chatFastMode = useChatStore(state => {
    const chat = state.chats.find(c => c.id === conversationId);
    return chat?.flags?.fastMode || false;
  });
  
  // Get fast mode preference for new conversations from chat input store
  // setParameter stores values in selectedParameters
  const newConvoFastMode = useChatInputStore(state => 
    state.inputStates?.[contextId]?.selectedParameters?.fastMode || false
  );
  const setParameter = useChatInputStore(state => state.setParameter);
  
  // Use the appropriate fast mode based on whether it's a new or existing conversation
  const isNewConversation = !conversationId || conversationId === 'new';
  const isFastMode = isNewConversation ? newConvoFastMode : chatFastMode;
  
  // Animation refs
  const hoverScaleAnim = useRef(new Animated.Value(1)).current;

  const { addToast } = useToastStore(state => state.actions);
  const requireAuth = useRequireAuth();

  // Determine if button should be disabled (either externally disabled or incompatible model)
  const isButtonDisabled = isDisabled || !isModelCompatible;
  
  const handlePress = useCallback(() => {
    // Fast mode is a per-user / per-conversation preference; gate before
    // we touch any store / socket so the toggle doesn't half-apply.
    const gated = requireAuth(() => true, {
      source: 'composer_fast_mode',
      title: t('chatInput.authCta.fastModeTitle'),
      subtitle: t('chatInput.authCta.fastModeSubtitle'),
      icon: 'bolt',
    });
    if (gated !== true) return;

    const newValue = !isFastMode;
    
    // Show toast on mobile for disabled state (since tooltips don't work on native or mobile web)
    const isMobile = Platform.OS !== 'web' || isXS;
    if (isButtonDisabled && isMobile) {
      if (!isModelCompatible) {
        addToast(t('chatInput.fastMode.onlyForAuto'), 'default');
      }
      return;
    }
    
    if (isButtonDisabled) {
      return;
    }
    
    console.log('[FastModeButton] handlePress:', {
      isNewConversation,
      conversationId,
      currentFastMode: isFastMode,
      newValue,
      userId,
      selectedModelIds,
      isModelCompatible,
    });
    
    // Show brief toast on mobile only (tooltip handles this on desktop)
    if (isMobile) {
      const toastMessage = newValue 
        ? t('chatInput.fastMode.onDescription') 
        : t('chatInput.fastMode.offDescription');
      addToast(toastMessage, 'default');
    }
    
    if (isNewConversation) {
      // For new conversations, store the preference in chat input store
      // This will be picked up when the message is sent
      setParameter('fastMode', newValue);
    } else {
      // For existing conversations, emit socket event
      if (!userId) {
        console.warn('[FastModeButton] Cannot toggle fast mode - userId not available');
        addToast(t('chatInput.fastMode.toggleFailed'), 'error');
        return;
      }
      if (!conversationId) {
        console.warn('[FastModeButton] Cannot toggle fast mode - conversationId not available');
        addToast(t('chatInput.fastMode.toggleFailed'), 'error');
        return;
      }
      console.log('[FastModeButton] Emitting toggleFastMode:', { userId, conversationId, newValue });
      socketManager.emitToggleFastMode(userId, conversationId, newValue);
    }
  }, [isButtonDisabled, conversationId, isNewConversation, userId, isFastMode, setParameter, addToast, selectedModelIds, isModelCompatible, isXS, requireAuth, t]);

  const handleHoverIn = useCallback(() => {
    Animated.spring(hoverScaleAnim, {
      toValue: 1.05,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  }, [hoverScaleAnim]);

  const handleHoverOut = useCallback(() => {
    Animated.spring(hoverScaleAnim, {
      toValue: 1,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  }, [hoverScaleAnim]);
  
  const tooltipLabel = !isModelCompatible
    ? t('chatInput.fastMode.onlyForAuto')
    : isFastMode 
      ? t('chatInput.fastMode.tooltipOn') 
      : t('chatInput.fastMode.tooltipOff');
  const activeColor = '#f59e0b'; // Amber/yellow for lightning
  const inactiveColor = colors.text_color_secondary;

  return (
    <Tooltip label={tooltipLabel} disabled={false}>
      <Animated.View style={{ transform: [{ scale: hoverScaleAnim }] }}>
        <Pressable 
          style={[
            styles.button,
            {
              backgroundColor: isFastMode && !isButtonDisabled ? `${activeColor}18` : 'transparent',
            },
            isButtonDisabled && styles.buttonDisabled,
          ]}
          onPress={handlePress}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          disabled={Platform.OS === 'web' && isButtonDisabled}
          accessibilityLabel={tooltipLabel}
          accessibilityRole="button"
          accessibilityState={{ disabled: isButtonDisabled, selected: isFastMode }}
        >
          {/* Lightning bolt icon - solid when active for clear visual feedback */}
          <Icon 
            name="bolt"
            size={16}
            solid={!!(isFastMode && !isButtonDisabled)}
            color={isFastMode && !isButtonDisabled ? activeColor : inactiveColor}
          />
        </Pressable>
      </Animated.View>
    </Tooltip>
  );
}

export default React.memo(FastModeButton);
